import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { Inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, AbstractControl, Validators, AsyncValidatorFn, ValidationErrors, Form } from '@angular/forms';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

import { IRoleGroupsMapping } from '@app/admin/users/role-groups-mapping.component';
import { IDoorEventMotionState } from '@app/admin/alerts-platform/shared/alert-details-form/alert-details-form.component';

import { IAllowedCarrier } from '@app/load-board/load-board.service';
import { ServerApi } from './server-api';

import { parameterize, rawPhoneFormat } from '../lib/utils';
import { CANADIAN_CYCLES, validateCanadianCycleSwitch } from '../lib/compliance-utils';

import { DATA_RENTENTION_DURATION } from '@app/admin/security/index.interface';

export const EMAIL = /^([a-zA-Z0-9_\.\-\+\'])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,})+$/;
const USERNAME = /^(\w|\.|\-)+$/;
export const PHONE = /^\([2-9][0-9]{2}\) *[0-9]{3}-[0-9]{4}$/;
export const NEW_PHONE_REGEX = /^((\+)?\d{0,2} )?[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
export const NOT_DIGITS = /\D/g;
export const PHONE_DIGITS_ONLY = /^(\d{0,2})?(\d{3})(\d{3})(\d{4})$/;
export const INTL_PHONE_FORMAT = /^\d{3}-\d{3}-\d{4}$/;
const DOT_ID = /^\d+$/;
const PASSWORD = /[^\s]/;
export const URL = /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/;
export const APP_LINK = /^[a-zA-Z0-9]+(:\/\/){1}/;
const ALPHANUMERIC = /^[0-9a-zA-Z]+$/;
export const NUMERIC = /^\d+(\.\d+)?$/;
const VIN_PATTERN_REGEX = /[`=\s\-~_+!@#$%^&*(),.?":{}|<>.,;'/\ioq\IO\][Q\\]/;
const LOCATION = /^[0-9a-zA-Z, .]+$/;
const VIN_TRANSLITERATION_MAPPING: { [key: string]: number } = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2,
  L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 }
;
const VIN_WEIGHTS: number[] = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
const LICENSE_MAX_CHARS_LIMIT = 20;

interface INotAvailableValidation {
  notAvailable: boolean;
}
type NotAvailableAsyncValidationFunc<T> = (contol: FormControl) => Promise<T>;

interface IInvalidNameValidation {
  invalidName: boolean;
}

interface IBlankValidation {
  blank: boolean;
}

interface IMinLengthValidation {
  minLength: boolean;
}

interface IAssetIDValidation {
  taken: boolean;
}

export interface IRecipientValidation {
  invalidRecipients: boolean;
}

export interface IRecipientEntityValidation {
  empty?: boolean;
  invalid?: boolean;
}

interface ILicenseValidation {
  groupRequired?: boolean;
  licenseStateRequired?: boolean;
  licenseNumberRequired?: boolean;
  invalidLicenseNumber?: boolean;
}

interface ICardApplicationValidation {
  noBusinessOwner?: boolean;
  noLinkedBankAccountAndDocumentUploaded?: boolean;
}

interface IAuxValidation {
  nameRequired?: boolean;
  signalReportingRequired?: boolean;
}

type CheckAssetAvailabilityType = (param: string, oldId: string) => Promise<IAssetIDValidation>;

@Injectable({ providedIn: 'root' })
export class CustomValidators {

  constructor(@Inject(ServerApi) private api) { }

  public isEqual(group) {
    const passwordControl = group.controls['password'];
    const confirmPasswordControl = group.controls['confirm_password'];

    if (!(confirmPasswordControl.hasError('required'))) {
      if (passwordControl.value == confirmPasswordControl.value) return null;

      return { isEqual: true };
    }
  }

  public isEmailOrUserNameSet() {
    return this.isEmailOrUserNameSetValidator.bind(this);
  }

  public isAuxNameAndSignalReportingRequired(): IAuxValidation | null {
    return this.isAuxNameAndSignalReportingSetValidator.bind(this);
  }

  public isLicenseSet(): (group: FormGroup) => ILicenseValidation {
    return this.isLicenseSetValidator.bind(this);
  }

  public isLicenseStateOrNumberSet(): (group: FormGroup) => ILicenseValidation {
    return this.licenseValidator.bind(this);
  }

  public userNameFormat() {
    return this.regexFieldValidator.bind(this, USERNAME);
  }

  public emailFormat() {
    return this.regexFieldValidator.bind(this, EMAIL);
  }

  public regexMultiFieldValueValidator(control: FormControl): IRecipientValidation {
    return this.regexMultiFieldValidator(control, EMAIL);
  }

  public phoneFormat() {
    return this.regexFieldValidator.bind(this, PHONE);
  }

  public newPhoneFormat() {
    return this.regexFieldValidator.bind(this, NEW_PHONE_REGEX);
  }

  public intlPhoneFormat(regexPattern: RegExp = INTL_PHONE_FORMAT) {
    return this.regexFieldValidator.bind(this, regexPattern)
  }

  public dotIdFormat() {
    return this.regexFieldValidator.bind(this, DOT_ID);
  }

  public dotIdIsBlank(): (control: any) => { invalid: boolean } {
    return function validator(control: any): { invalid: boolean } {
      const value: IAllowedCarrier = control.value;
      return value && !isEmpty(value.id) ? null : { invalid: true };
    };
  }

  public atLeastOneSpeedLimit(): ValidatorFn {
    return this.checkIfAtleastOneSpeedLimit.bind(this);
  }

  public urlFormat() {
    return this.regexFieldValidator.bind(this, URL);
  }

  public secretFormat() {
    return this.regexFieldValidator.bind(this, ALPHANUMERIC);
  }

  public validateCycleSwitch(currentLogCycle: string, oldCycle: string, recentBreakDuration: number) {
    return this.isValidCycleSwitched.bind(this, currentLogCycle, oldCycle, recentBreakDuration);
  }

  public checkAvailability(param = 'email', oldEmail = null) {
    const validator = this.checkUserParamAvailability.bind(this, param, oldEmail);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public checkProfileNameAvailability(param: string) {
    const validator = this.checkProfileAvailability.bind(this, param);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public isFMIdUnique(existingId = null): AsyncValidatorFn {
    const validator = this.checkFMIdentityAvailability.bind(this, existingId);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public checkDriversLicense(oldLicenseNumber, oldLicenseState) {
    const validator = this.checkDriversLicenseAvailability.bind(this, oldLicenseNumber, oldLicenseState);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public checkVehicleAvailability(param = 'number', oldNumber = null) {
    const validator = this.checkVehicleNumberAvailability.bind(this, param, oldNumber);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public checkAssetAvailability(param: string = 'name', oldId: string = null): CheckAssetAvailabilityType {
    const validator: NotAvailableAsyncValidationFunc<IAssetIDValidation> =
      this.checkAssetIDAvailability.bind(this, param, oldId);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public isBlank(): (control: FormControl) => IBlankValidation {
    return this.isEmptyInputValue.bind(this);
  }

  public isMinLength(minLength: number): ValidatorFn {
    return this.validateMinlength.bind(this, minLength);
  }

  public passwordFormat() {
    return this.regexFieldValidator.bind(this, PASSWORD);
  }

  public uniqueField(group, reservedFields) {
    return this.isUniqueField.bind(this, group, reservedFields);
  }

  public validName(): (control: FormControl) => IInvalidNameValidation {
    return this.isValidName.bind(this);
  }

  public maxFromMask(max: number): ValidatorFn {
    return this.checkMaxNumberFromMaskValue.bind(this, max);
  }

  public isGeofenceNameAvailable(oldName: string): (...args: any[]) => Promise<INotAvailableValidation> {
    const validator: NotAvailableAsyncValidationFunc<INotAvailableValidation> =
      this.checkGeofenceNameAvailability.bind(this, oldName);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public isGroupNameAvailable(oldName: string = null): (...args: any[]) => Promise<INotAvailableValidation> {
    const validator: NotAvailableAsyncValidationFunc<INotAvailableValidation> =
      this.checkGroupNameAvailability.bind(this, oldName);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public validRoleGroupsMapping(required: boolean): { invalid: true } | null {
    return this.isValidRoleGroupsMapping.bind(this, required);
  }

  public validateVinPattern(): ValidatorFn {
    return this.isValidVinPattern.bind(this);
  }

  public validateVinCheckDigit(): ValidatorFn {
    return this.isValidVinCheckDigit.bind(this);
  }

  public alphanumeric(): ValidatorFn {
    return this.regexFieldValidator.bind(this, ALPHANUMERIC);
  }

  public numeric(control: FormControl): { nonNumeric: true } | null {
    return control.value && !control.value.match(NUMERIC) ? { nonNumeric: true } : null;
  }

  public affixValueRequired(control: FormControl): {required: boolean} | null {
    return control.value && control.value.value ? null : {required: true};
  }

  /**
   * Validates that a field has a value only if one or more other fields have a value.
   * @param coRequiredControls Getter function for the fields
   * @param control The control to validate
   * @returns The errors, if any are present
   */
  public coRequiredValidator(
    coRequiredControls: () => FormControl[],
    control: FormControl,
  ): { [error: string]: boolean | string } | null {
    const coFieldSet: boolean = coRequiredControls().reduce(
      (curVal: boolean, coField: FormControl) => {
        if (!!coField?.value) {
          curVal = true;
        } else if (coField?.errors?.coFieldRequired) {
          coField.updateValueAndValidity();
        }
        return curVal;
      }, false,
    );
    return coFieldSet ? (Validators.required(control) ? {
      coFieldRequired: true,
    } : null) : null;
  }

  public isChecked(): (control: AbstractControl) => {[key: string]: boolean} | null {
    return (control: AbstractControl): {[key: string]: boolean} | null => {
      if (control.value !== true) {
        return { checked: false };
      }
      return null;
    };
  }

  public isValidLocation(): ValidatorFn {
    return this.regexFieldValidator.bind(this, LOCATION);
  }

  public atleastOneBusinessOwner(): ValidatorFn {
    return this.checkIfAtleastOneBusinessOwner.bind(this);
  }

  public doorEventAlertsAssetStateValidator(): ValidatorFn {
    return this.checkIfAnyAssetStateSelected.bind(this);
  }

  public hasEitherBankAccountLinkedOrDocumentUploaded(): ValidatorFn {
    return this.checkIfhasEitherBankAccountLinkedOrDocumentUploaded.bind(this);
  }

  public isSpeedOrEngineOptionChecked(): {required: boolean} | null {
    return this.isSpeedOrEngineOptionCheckedValidator.bind(this);
  }

  public isOneCheckboxTrue(): {requireOneCheckboxToBeChecked: boolean} | null {
    return this.requireOneCheckboxToBeCheckedValidator.bind(this);
  }

  public checkDotIdClaimed(companyId: number): AsyncValidatorFn {
    const validator: NotAvailableAsyncValidationFunc<INotAvailableValidation> =
    this.isDotIdClaimed.bind(this, companyId);
    return this.createDebouncedAsyncValidator(validator, 500);
  }

  public checkFMIdentityAvailability(oldId: string, formControl: FormControl): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const params = new Map();
      const newId = formControl.value;
      params.set('company_reference_id', newId);
      if (newId === oldId || !newId) {
        resolve(null);
      } else if (newId) {
        this.api
          .get('users/fleet_id_available', params)
          .subscribe(({body}: {body: {available: boolean}}) => {
            body.available ? resolve(null) : resolve({ taken: true });
          });
      }
    });
  }

  // Define the lessThanOrEqual validator
   // Custom validator function to check if the value of control1 is less than or equal to the value of control2
  public lessThanOrEqual(control1: AbstractControl, control2: AbstractControl): ValidationErrors | null {
    const value1 = control1.value;
    const value2 = control2.value;

    if (value1 !== null && value2 !== null && value1 > value2) {
      return { lessThanOrEqual: true };
    }

    return null;
  }


  // Validator function for compliance and maintenance retention
  public complianceMaintenanceRetention(complianceControl: AbstractControl, maintenceControl: AbstractControl): ValidatorFn {
    return (): ValidationErrors | null => {
      const complianceValue: string = complianceControl.value;
      const maintenanceValue: string = maintenceControl.value;

      const numericValuebyMonths = {
        [DATA_RENTENTION_DURATION.DEFAULT] : Infinity,
        [DATA_RENTENTION_DURATION.TWO_WEEKS] : 0.5,
        [DATA_RENTENTION_DURATION.ONE_MONTH] : 1,
        [DATA_RENTENTION_DURATION.THREE_MONTH] : 3,
        [DATA_RENTENTION_DURATION.SIX_MONTHS] : 6,
        [DATA_RENTENTION_DURATION.ONE_YEAR] : 12,
      };

      if (complianceValue !== null && maintenanceValue !== null) {
        if (numericValuebyMonths[complianceValue] < numericValuebyMonths[maintenanceValue]) {
          complianceControl.setErrors({lowerComplianceRetention: true});
          complianceControl.markAsTouched();
          maintenceControl.setErrors({lowerComplianceRetention: true});
          maintenceControl.markAsTouched();
          return { lowerComplianceRetention: true };
        } else {
          complianceControl.setErrors(null);
          maintenceControl.setErrors(null);
        }
      }
      return null;
    };
  }

  public validateEmail(email: string): boolean {
    return !!email.match(EMAIL);
  }

  public validatePhoneNumber(phoneNumber: string, validator: string ='^\\d{10}$'): boolean {
    const phoneNumbersFragments = phoneNumber.split(' ');
    const unformattedNumber =
      phoneNumbersFragments[phoneNumbersFragments.length - 1];
    const rawNumber = unformattedNumber.replace(NOT_DIGITS, '');
    return !!rawNumber.match(validator)?.length;
  }

  private checkMaxNumberFromMaskValue (max: number, control: AbstractControl): ValidationErrors | null {
    const value: string = control.value;
    if (!value) return null;

    const matches = (value + '').match(/\d+/g);
    if (!matches) return null;

    const digitsfromValue: number = Number(matches.join(''));
    return digitsfromValue > max ? { max: { max, actual: value } } : null;
  }

  private isDotIdClaimed(companyId: number, control: FormControl): Promise<INotAvailableValidation> {
    const dotId = control.value;
    return new Promise((resolve, reject) => {
      const params: Map<string, string> = new Map();
      if (!dotId) {
        resolve(null);
        return;
      }
      params.set('dot_id', dotId);
      this.api.get(`companies/${companyId}/dot_ids/validate`, params).subscribe(
        () => resolve(null),
        () => resolve({ notAvailable: true }),
      );
    });
  }

  private isValidVinPattern(control: FormControl): { invalidVinPattern: true } | null {
    return VIN_PATTERN_REGEX.test(control.value) ? { invalidVinPattern: true } : null;
  }

  private isValidVinCheckDigit(control: FormControl): { invalidVin: true } | null {
    let vin = control.value;
    if (!vin) return null;
    if (vin.length >= 17) {
      vin = vin.toUpperCase();
      let total = 0;
      for (let i = 0; i < vin.length; i++) {
        const char = vin[i];
        const charValue = (VIN_TRANSLITERATION_MAPPING[char] || Number(char));
        const product = charValue ? VIN_WEIGHTS[i] * charValue : 0;
        total += product;
      }

      let checkDigit: number | string = total % 11;
      checkDigit = (total % 11) == 10 ? 'X' : String(checkDigit);

      return checkDigit === vin[8] ? null : { invalidVin: true };
    } else {
      return { invalidVin: true };
    }
  }

  private isValidRoleGroupsMapping(required: boolean, formGroup: FormGroup): { invalid: true } | null {
    if (formGroup?.value) {
      const mappingList: IRoleGroupsMapping[] = formGroup.value;
      const invalidMapping = mappingList
        .some((mapping: IRoleGroupsMapping) => !mapping.role || required && !mapping.groups?.length);
      return invalidMapping ? { invalid: true } : null;
    } else {
      return { invalid: true };
    }
  }

  private isValidCycleSwitched(currentLogCycle: string, oldCycle: string, recentBreakDuration: number, control: FormControl) {
    let result: { invalidSwitch: boolean } = null;
    const newCycle: string = control.value;

    if (
      newCycle === currentLogCycle ||
      recentBreakDuration == null ||
      !(currentLogCycle in CANADIAN_CYCLES) ||
      currentLogCycle !== oldCycle
    ) return null;

    result = validateCanadianCycleSwitch(currentLogCycle, newCycle, recentBreakDuration) > 0 ? { invalidSwitch: true } : null;
    return result;
  }

  private checkGeofenceNameAvailability(oldName: string, control: FormControl): Promise<INotAvailableValidation> {
    return new Promise(resolve => {
      const value: string = control.value;
      if (value && !control.hasError('invalid')) {

        if (oldName && value === oldName) {
          resolve(null);
          return;
        }
        const params: Map<string, string> = new Map();
        params.set('name', control.value.trim());
        this.api
          .get('geofences/name_available', params)
          .subscribe(response => {
            response = response.body;
            if (response.available) {
              resolve(null);
            } else {
              resolve({ notAvailable: true });
            }
          });
      } else {
        resolve(null);
      }
    });
  }

  private checkGroupNameAvailability(oldGroupName: string, control: FormControl): Promise<INotAvailableValidation> {
    return new Promise(resolve => {
      const value: string = control.value;
      if (value && !control.hasError('invalid')) {
        if (oldGroupName && value === oldGroupName) {
          resolve(null);
          return;
        }
        const params: Map<string, string> = new Map();
        params.set('name', value);
        this.api.get('groups/available', params).subscribe(
          () => resolve(null),
          (res: HttpErrorResponse) => {
            const error: string = res.error.error;
            if (error.match(/name already exists/)) {
              resolve({ notAvailable: true });
            } else resolve(null);
          },
        );
      } else {
        resolve(null);
      }
    });
  }

  private regexFieldValidator(regexPatt, control) {
    let value = control.value;
    if (typeof value === 'object' && value !== null) {
      value = get(control, 'value.id');
      const validField = value && value.match(regexPatt);
      return validField ? null : { invalid: true };
    }

    if (value) {
      const validField = value.match(regexPatt);
      return validField ? null : { invalid: true };
    }
    return null;
  }

  private regexMultiFieldValidator(control: FormControl, regexPatt: RegExp): IRecipientValidation {
    const regex = new RegExp(regexPatt);
    let invalidRecipient: boolean = false;
    if (control.value && control.value.length > 0) {
      control.value.forEach((val): object => {
        if (!isNaN(val)) return;
        if (!val.match(regex)) invalidRecipient = true;
      });
    }
    return invalidRecipient ? { invalidRecipients: true } : null;
  }

  private checkVehicleNumberAvailability(param, oldNumber, control) {
    return new Promise((resolve, reject) => {
      const value = control.value;
      if (value && !control.hasError('invalid')) {

        if (oldNumber && value === oldNumber) {
          resolve(null);
          return;
        }
        const params = new Map();
        params.set(param, value);
        this.api.get('vehicles/available', params).subscribe(
          () => { resolve(null); }, response => {
            if (response.error) {
              resolve({ taken: true });
            } else {
              resolve({ unknown: true });
            }
          },
        );
      } else {
        resolve(null);
      }
    });
  }

  private checkAssetIDAvailability(param: string, oldId: string, control: FormControl): Promise<IAssetIDValidation> {
    return new Promise((resolve, reject) => {
      const value: string = control.value;
      if (value && !control.hasError('invalid')) {
        if (oldId && value === oldId) {
          resolve(null);
          return;
        }
        const params: Map<string, string> = new Map<string, string>();
        params.set(param, value);
        this.api.get('assets/name_available', params).subscribe((response: HttpResponse<{ ['available']: boolean }>) => {
          const { available } = response.body;
          available ? resolve(null) : resolve({ taken: true });
        });
      } else {
        resolve(null);
      }
    });
  }

  private checkUserParamAvailability(param: string, oldEmail: string, control: FormControl) {
    return new Promise((resolve, reject) => {
      const value: string = control.value;
      if (value && !control.hasError('invalid')) {
        if (oldEmail && value === oldEmail) {
          resolve(null);
          return;
        }
        const params: Map<string, string> = new Map();
        params.set(param, value);
        this.api.get('users/available', params).subscribe(
          () => { resolve(null); }, (res: HttpErrorResponse) => {
            const error: string = res.error.error;
            if (error.match(/is not available/)) {
              resolve({ available: true });
            } else if (error.match(/is not deliverable/)) {
              resolve({ deliverable: true });
            }
          },
        );
      } else {
        resolve(null);
      }
    });
  }

private checkProfileAvailability(param: string, control: FormControl): Promise<null | { availabilityError: string }> {
  return new Promise((resolve) => {
    const value: string = control.value;

    if (!value || control.hasError('invalid')) {
      resolve(null);
      return;
    }

    const params: Map<string, string> = new Map();
    params.set(param, value);

    this.api.get('profiles/available', params).subscribe({
      next: (res: any) => {
        const result = res?.body?.result;
        const error = res?.body?.error || res?.body?.error?.details;
        const errorMessage = $localize`Profile name already exists`;

        if (result === true) {
          resolve(null);
        } else if (typeof error === 'string' && error.includes(errorMessage.toLocaleLowerCase())) {
          resolve({ availabilityError: errorMessage });
        } else {
          resolve(null);
        }
      },
      error: (err: HttpErrorResponse) => {
        const errorMessage = err?.error?.error || '';
        if (errorMessage.includes(errorMessage.toLocaleLowerCase())) {
          resolve({ availabilityError: errorMessage });
        } else {
          resolve(null);
        }
      }
    });
  });
}

  private createDebouncedAsyncValidator(
    validatorFn: (...args: any[]) => Promise<any>,
    wait: number,
  ): (...args: any[]) => Promise<any> {
    let timer = null;
    return (...args) => {
      return new Promise((resolve, reject) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          validatorFn(...args).then(resolve, reject);
          timer = null;
        }, wait);
      });
    };
  }

  private checkDriversLicenseAvailability(oldLicenseNumber, oldLicenseState, group) {
    return new Promise((resolve, reject) => {
      const licenseNumber = group.controls.licenseNumber.value;
      const licenseState = group.controls.licenseState.value;
      const params = new Map();
      params.set('drivers_license_number', licenseNumber);
      params.set('drivers_license_state', licenseState);
      if (oldLicenseNumber === licenseNumber && oldLicenseState === licenseState) {
        resolve(null);
      } else if (licenseState && licenseNumber) {
        this.api
          .get('users/license_available', params)
          .subscribe(response => {
            response = response.body;
            if (response.available) {
              resolve(null);
            } else {
              resolve({ notAvailable: true });
            }
          });
      } else if (licenseNumber) {
        resolve({ required: true });
      } else {
        resolve(null);
      }
    });
  }

  private isAuxNameAndSignalReportingSetValidator(group: FormGroup): IAuxValidation | null {
    const name: FormControl = group.controls.name as FormControl;
    const enable: FormControl = group.controls.enable as FormControl;
    const signalReporting: FormControl = group.controls.signalReporting as FormControl;
    let isNameRequired: boolean = false;
    let isSignalReportingRequired: boolean = false;

    if (enable?.value && !name?.value) {
      isNameRequired = true;
    }
    if (enable?.value && !signalReporting?.value) {
      isSignalReportingRequired = true;
    }

    if (isNameRequired || isSignalReportingRequired) {
      return {
        ...(isNameRequired && { nameRequired: isNameRequired }),
        ...(isSignalReportingRequired && { signalReportingRequired: isSignalReportingRequired }),
      };
    }
    return null;
  }

  private isEmailOrUserNameSetValidator(group) {
    const email = group.controls.email;
    const userName = group.controls.userName;
    if (email?.value || userName?.value) {
      return null;
    }
    return { required: true };
  }

  private isLicenseSetValidator(group: FormGroup): ILicenseValidation {
    const licenseState: FormControl = group.controls.licenseState as FormControl;
    const licenseNumber: FormControl = group.controls.licenseNumber as FormControl;
    let validation: ILicenseValidation;
    if (!licenseState.value && !licenseNumber.value) {
      validation = { groupRequired: true };
    } else if (!licenseState.value) {
      validation = { licenseStateRequired: true };
    } else if (!licenseNumber.value) {
      validation = { licenseNumberRequired: true };
    } else if (!ALPHANUMERIC.test(licenseNumber.value) || licenseNumber.value.length > LICENSE_MAX_CHARS_LIMIT) {
      validation = { invalidLicenseNumber: true };
    }
    return validation;
  }

  private licenseValidator(group: FormGroup): ILicenseValidation {
    const licenseState: FormControl = group.controls.licenseState as FormControl;
    const licenseNumber: FormControl = group.controls.licenseNumber as FormControl;
    let validation: ILicenseValidation;
    if (!licenseState.value && licenseNumber.value) {
      validation = { licenseStateRequired: true };
    } else if (licenseState.value && !licenseNumber.value) {
      validation = { licenseNumberRequired: true };
    } else if (licenseNumber.value && (!ALPHANUMERIC.test(licenseNumber.value)
     || licenseNumber.value.length > LICENSE_MAX_CHARS_LIMIT)) {
      validation = { invalidLicenseNumber: true };
    }
    return validation;
  }

  private isEmptyInputValue(control: FormControl): IBlankValidation {
    const value = control.value;
    return (typeof value === 'string' && value.length && !value.trim().length) ? { blank: true } : null;
  }

  private validateMinlength(minLength: number, control: FormControl): IMinLengthValidation {
    const value = control.value;
    return (typeof value === 'string' && value.length && value.trim().length < minLength) ? { minLength: true } : null;
  }

  private isUniqueField(group, reservedFields, control) {
    const customControl = control;
    let isUnique = true;
    forEach(group.controls, element => {
      if (customControl !== element && parameterize(customControl.value) === parameterize(element.value) && element.value) {
        isUnique = false;
      }
    });
    if (isUnique) {
      forEach(reservedFields, reservedField => {
        if (parameterize(customControl.value) === parameterize(reservedField) && customControl.value) {
          isUnique = false;
        }
      });
      return isUnique ? null : { notunique: true };
    } else {
      return { notunique: true };
    }
  }

  private isValidName(control: FormControl): IInvalidNameValidation {
    const value = control.value;
    const regex = new RegExp(`(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|
    [\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|
    \u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e
    |\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01\uddff]|\ud83c
    [\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32\ude02]|\ud83c\ude1a
    |\ud83c\ude2f|\ud83c[\ude32-\ude3a]|[\ud83c[\ude50\ude3a]|\ud83c[\ude50-\ude51]
    |\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122
    |\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50
    |\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf
    |\u2934|\u2935|[\u2190-\u21ff])`, 'g');

    return value && regex.test(value) ? { invalidName: true } : null;
  }

  private checkIfAtleastOneBusinessOwner(formGroup: FormGroup): ICardApplicationValidation{
    const atLeastOneBusinessOwner: boolean = formGroup.value.executive_team_member.is_beneficial_owner ||
    formGroup.value.beneficial_owners.length > 0;
    return atLeastOneBusinessOwner ? null : {noBusinessOwner: true};
  }

  private checkIfhasEitherBankAccountLinkedOrDocumentUploaded(formGroup: FormGroup): ICardApplicationValidation{
    const hasEitherBankAccountLinkedOrDocumentUploaded: boolean = formGroup.value.payment_method_id ||
    formGroup.value.company_card_document_ids.length > 0;
    return hasEitherBankAccountLinkedOrDocumentUploaded ? null : { noLinkedBankAccountAndDocumentUploaded: true };
  }

  private isSpeedOrEngineOptionCheckedValidator(group: FormGroup): {required: boolean} | null {
    if (group.controls.vehicleMotion.value && !group.controls.movementSpeed.value) return { required: true };
    return (group.controls.vehicleMotion.value || group.controls.engineStart.value) ? null : { required: true };
  }

  private checkIfAtleastOneSpeedLimit(formGroup: FormGroup): { error: boolean } | null {
    if (!formGroup.value.truckSpeedLimit && !formGroup.value.vehicleSpeedLimit) return { error: true };
    return null;
  }

  private checkIfAnyAssetStateSelected(formGroup: FormGroup): {required: boolean} | null {
    const doorEventAlertState: IDoorEventMotionState = formGroup.value;
    if (formGroup.value && formGroup.value.length > 1) {
      return (formGroup.value.some((state: IDoorEventMotionState): boolean => state.selected)) ? null : { required: true };
    } else return (doorEventAlertState && doorEventAlertState.selected) ? null : { required: true };
  }

  // tslint:disable-next-line: max-line-length
  private requireOneCheckboxToBeCheckedValidator(formGroup: FormGroup): {requireOneCheckboxToBeChecked: boolean} | null {
    return Object.values(formGroup.value).some(value => value) ? null : { requireOneCheckboxToBeChecked: true };
  }
}
